import { API_BASE_URL } from '../config';

const withJson = (headers = {}) => ({
  ...headers,
  'Content-Type': 'application/json',
});

const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.detail || `Request failed (${response.status})`);
  }
  return data;
};

export const listConfigurableDefinitions = async (headers) => {
  const response = await fetch(`${API_BASE_URL}/api/v1/products/configurable/definitions`, { headers });
  return parseResponse(response);
};

export const getConfigurableDefinitionByProduct = async (productCode, headers) => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/products/configurable/by-product/${encodeURIComponent(productCode)}`,
    { headers },
  );
  return parseResponse(response);
};

export const getConfigurableDefinition = async (definitionId, headers) => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/products/configurable/definitions/${definitionId}`,
    { headers },
  );
  return parseResponse(response);
};

export const createConfigurableDefinition = async (payload, headers) => {
  const response = await fetch(`${API_BASE_URL}/api/v1/products/configurable/definitions`, {
    method: 'POST',
    headers: withJson(headers),
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
};

export const updateConfigurableDefinition = async (id, payload, headers) => {
  const response = await fetch(`${API_BASE_URL}/api/v1/products/configurable/definitions/${id}`, {
    method: 'PUT',
    headers: withJson(headers),
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
};

export const publishConfigurableDefinition = async (id, headers) => {
  const response = await fetch(`${API_BASE_URL}/api/v1/products/configurable/definitions/${id}/publish`, {
    method: 'POST',
    headers,
  });
  return parseResponse(response);
};

export const deleteConfigurableDefinition = async (id, headers) => {
  const response = await fetch(`${API_BASE_URL}/api/v1/products/configurable/definitions/${id}`, {
    method: 'DELETE',
    headers,
  });
  return parseResponse(response);
};

export const searchProductsSolr = async (payload, headers) => {
  const normalizedPayload = payload && typeof payload === 'object' ? payload : {};
  const query = String(normalizedPayload.query || '').trim();
  const page = Math.max(1, Number(normalizedPayload.page || 1));
  const pageSize = Math.max(1, Math.min(100, Number(normalizedPayload.page_size || 25)));
  const filters = normalizedPayload.filters && typeof normalizedPayload.filters === 'object'
    ? normalizedPayload.filters
    : {};

  const mapProductsToSolrShape = (products = []) =>
    products.map((row) => ({
      id: row?.id,
      productCode: row?.product_code || '',
      name: row?.name || '',
      description: row?.description || '',
      category: row?.category || '',
      imageUrl: row?.image_url || '',
      manufacturerName: row?.manufacturer_name || '',
      manufacturerPartNumber: row?.manufacturer_part_number || '',
    }));

  const buildSimpleFacets = (docs = []) => {
    const tally = (key) => {
      const counts = new Map();
      docs.forEach((doc) => {
        const value = String(doc?.[key] || '').trim();
        if (!value) return;
        counts.set(value, (counts.get(value) || 0) + 1);
      });
      return Array.from(counts.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
    };
    return {
      category: tally('category'),
      manufacturerName: tally('manufacturerName'),
    };
  };

  // Primary path: Solr search
  try {
    const solrResponse = await fetch(`${API_BASE_URL}/api/v1/products/solr/search`, {
      method: 'POST',
      headers: withJson(headers),
      body: JSON.stringify(normalizedPayload),
    });
    const solrData = await parseResponse(solrResponse);
    const numFound = Number(solrData?.numFound || 0);
    const solrDocs = Array.isArray(solrData?.docs) ? solrData.docs : [];
    if ((numFound > 0 && solrDocs.length > 0) || !query) {
      return solrData;
    }
    // If Solr returns no usable docs for a typed query, fall back to DB search.
  } catch (err) {
    // Fall through to DB fallback below.
  }

  // Fallback path: DB product search endpoint (works even when Solr is unavailable/out-of-sync).
  const offset = (page - 1) * pageSize;
  const fallbackResponse = await fetch(
    `${API_BASE_URL}/api/v1/products/?limit=${pageSize}&offset=${offset}&search=${encodeURIComponent(query)}`,
    { headers },
  );
  const fallbackData = await parseResponse(fallbackResponse);
  const docsRaw = mapProductsToSolrShape(Array.isArray(fallbackData?.products) ? fallbackData.products : []);
  // For fallback, avoid over-filtering to ensure users can always recover results.
  // Solr still handles full facet+filter behavior when available.
  const docs = docsRaw;
  const totalCount = Number(fallbackData?.pagination?.total_count || docs.length || 0);

  return {
    collection: 'products_db_fallback',
    query,
    page,
    page_size: pageSize,
    numFound: totalCount,
    docs,
    facets: buildSimpleFacets(docsRaw),
    filters,
    fallback_used: true,
  };
};

export const addConfiguredItemToCart = async (definitionId, payload, headers) => {
  const response = await fetch(`${API_BASE_URL}/api/v1/products/configurable/${definitionId}/cart/add`, {
    method: 'POST',
    headers: withJson(headers),
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
};

export const listConfiguredCartItems = async (userId, headers) => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/products/configurable/cart?user_id=${encodeURIComponent(userId)}`,
    { headers },
  );
  return parseResponse(response);
};

export const updateConfiguredCartItem = async (cartItemId, payload, headers) => {
  const response = await fetch(`${API_BASE_URL}/api/v1/products/configurable/cart/${encodeURIComponent(cartItemId)}`, {
    method: 'PUT',
    headers: withJson(headers),
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
};
